import { useEffect, useMemo, useState } from "react";
import { runWidgetQuery, widgetKind } from "./dashboard-api";

/**
 * Runs every widget's query for a dashboard page and returns the results by
 * widget id.
 *
 * The fetching lives here rather than inside each widget on purpose: the grid
 * partitions widgets by whether they returned data, so a widget moves between
 * containers as its result arrives. If it owned its own fetch, that move would
 * unmount and remount it, firing the query again — and again on the next move.
 */
export default function useWidgetData(widgets, options) {
  const { variables, strategies, fallbackAccountId } = options;
  const [results, setResults] = useState({});

  // Bind to the values, not the object identities, so a re-render with an
  // equal-but-new options object does not re-run every query.
  const entityVar = variables?.entity;
  const varsKey = useMemo(() => JSON.stringify(variables || {}), [variables]);
  const stratKey = useMemo(() => JSON.stringify(strategies || {}), [strategies]);
  const idsKey = useMemo(() => (widgets || []).map((w) => w.id).join(","), [widgets]);

  useEffect(() => {
    if (!widgets?.length) return undefined;

    let cancelled = false;
    const controller = new AbortController();
    setResults({});

    // NerdGraph allows 25 *concurrent* requests per user and does not limit the
    // request rate, so the ceiling is how many are in flight at once. Eight
    // leaves room for the entity queries alongside these, and for New Relic's
    // separate advice to keep sustained NRQL concurrency low — a dashboard open
    // is a short burst, not sustained load.
    //
    // The 429s that originally forced this down to 4 came from ~16 queries
    // issued twice over, because resolving the entity name restarted the load
    // (see EntityPanel's `names` state). ~32 concurrent crosses the limit; 16
    // never did. runGraphQL still retries on 429 either way.
    const queue = [...widgets];
    const CONCURRENCY = 8;

    const worker = async () => {
      while (!cancelled) {
        const widget = queue.shift();
        if (!widget) return;
        try {
          const result = await runWidgetQuery(widget, {
            variables,
            strategies,
            fallbackAccountId,
            signal: controller.signal,
          });
          if (cancelled) return;
          setResults((prev) => ({
            ...prev,
            [widget.id]: { rows: result.rows, error: null },
          }));
        } catch (error) {
          if (cancelled || error?.name === "AbortError") return;
          setResults((prev) => ({
            ...prev,
            [widget.id]: { rows: [], error },
          }));
        }
      }
    };

    Array.from({ length: Math.min(CONCURRENCY, widgets.length) }, worker);

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, varsKey, stratKey, fallbackAccountId, entityVar]);

  return results;
}

/**
 * Whether the query came back with anything at all.
 *
 * A billboard reading 0 is a real measurement — "nothing is offline" — and is
 * not the same as a query that returned no rows, which means we do not know.
 * Only the latter is quiet.
 */
export function hasResult(rows) {
  return Boolean(rows?.length);
}

/**
 * Whether a result is worth repeating in the summary. Stricter than
 * hasResult: a zero belongs on the dashboard but adds nothing to a list of
 * findings printed next to an open issue.
 */
export function isNotable(kind, rows) {
  if (!rows?.length) return false;
  if (kind !== "billboard") return true;
  const row = rows[0] || {};
  const key = Object.keys(row).find((k) => typeof row[k] === "number");
  return key !== undefined && row[key] !== 0;
}

/** Splits a page's widgets by what came back. */
export function partitionWidgets(widgets, results) {
  const loud = [];
  const quiet = [];
  const pending = [];

  for (const widget of widgets || []) {
    const result = results[widget.id];
    if (!result) {
      pending.push(widget);
    } else if (result.error) {
      quiet.push({ widget, result, failed: true });
    } else if (hasResult(result.rows)) {
      loud.push({ widget, result });
    } else {
      quiet.push({ widget, result, failed: false });
    }
  }
  return { loud, quiet, pending };
}
